import { DatepickerInputValue } from '../types/components/common/datepicker';
import { AdditionalQuestionResponseFormData, AdditionalQuestionResponsePostData } from '../types/sdq';

// 氏名ではなく ID で子ども・記入者を識別する質問票。
// 4~11歳用/教師記入用 のみ ID 運用のため、質問票のキーで出し分ける。
export const ID_INPUT_SURVEY_TITLES = ['TEACHER_4_11'];

// 氏名の代わりにIDで識別する質問票かどうかを判定する。
// ページ側（respondentValuesの初期化）とコンポーネント側（表示切替）の両方が
// この判定を使うことで、判定ロジックが2箇所に分かれて食い違うのを防ぐ。
export const usesIdInput = (surveyTitle?: string): boolean => {
  return !!surveyTitle && ID_INPUT_SURVEY_TITLES.includes(surveyTitle);
};

export interface RespondentIdentifierValues {
  last_name?: string;
  first_name?: string;
  signature?: string;
  child_id?: string;
  teacher_id?: string;
  birth_date?: DatepickerInputValue;
  sex_of_child?: string;
  relationship?: string;
  relationship_detail?: string;
}

/**
 * 質問票を切り替えても前の質問票で入力した氏名・IDがフォーム状態に残ったままだと、
 * 画面には表示されていない項目が送信・保存されてしまう（氏名を集めない質問票で
 * 氏名が保存されるなど）。選択中の質問票が使わない側の識別項目を空にして返す。
 *
 * ID運用の質問票（TEACHER_4_11）は氏名・署名に加えて、お誕生日・性別・
 * お子さんとの関係も画面に表示しないため、これらも合わせて空にする。
 * 逆に氏名運用の質問票へ戻す場合はこれらの項目を引き続き表示するため、
 * 入力値をそのまま保持する。
 */
export const clearUnusedIdentifierFields = (
  surveyTitle: string | undefined,
  values: RespondentIdentifierValues
): RespondentIdentifierValues => {
  const useIdInput = usesIdInput(surveyTitle);
  return {
    last_name: useIdInput ? '' : values.last_name,
    first_name: useIdInput ? '' : values.first_name,
    signature: useIdInput ? '' : values.signature,
    child_id: useIdInput ? values.child_id : '',
    teacher_id: useIdInput ? values.teacher_id : '',
    birth_date: useIdInput ? { startDate: undefined, endDate: undefined } : values.birth_date,
    sex_of_child: useIdInput ? '' : values.sex_of_child,
    relationship: useIdInput ? '' : values.relationship,
    relationship_detail: useIdInput ? '' : values.relationship_detail
  };
};

/**
 * 追加設問の回答をフォーム内部の形（複数選択は choices: number[]）から
 * POST データの形（1選択肢につき1行の {question_relation, choice}）へ変換する。
 * 複数選択で0件選択の場合は未選択を表す1行 {choice: undefined} を送る
 * （バックエンドとの契約: POST では同じ question_relation の行を複数送ってよく、
 * 未選択なら choice: undefined の1行を送る）。
 */
export const toAdditionalQuestionResponsesPostData = (
  responses: AdditionalQuestionResponseFormData[]
): AdditionalQuestionResponsePostData[] => {
  return responses.flatMap((response) => {
    if (response.choices !== undefined) {
      if (response.choices.length === 0) {
        return [{ question_relation: response.question_relation, choice: undefined }];
      }
      return response.choices.map((choice) => ({
        question_relation: response.question_relation,
        choice
      }));
    }
    return [{ question_relation: response.question_relation, choice: response.choice }];
  });
};
